import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { Sezionealbonazenticoop } from '../../../models/interfaces/dati-business.interface';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { posizAlboEntiRegex } from '@shared/constants/regex.const';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { cloneDeep } from 'lodash-es';
import { getDatiIdentificativiControl } from '../validators/helpers/dati-identificativi-form-validators.helper';

const ALBO_WARNING_DESC = "La cooperativa non è iscritta all'Albo delle Società Cooperative.";

export const setDatiIdentificativiMutualitaEnteListeners = (form: FormGroup, store: Store<Revisione53State>, toastrService: ToastrService): void => {
    my_CkMutualPrev(form, store, toastrService);
    my_PosizAlboEntiEnableDisable(form);
    my_SezEnte(form, store);
    my_PosizAlboEnti(form, store);
    my_CodAttiv(form, store);
    my_RegimeDerogatorioProvalenza(form, store);
};

const my_CkMutualPrev = (form: FormGroup, store: Store<Revisione53State>, toastrService: ToastrService): void => {
    const ckMutualControl = getDatiIdentificativiControl(form, 'my_CkMutualPrev');
    ckMutualControl.valueChanges.pipe(withChangeDebounced(ckMutualControl)).subscribe((my_CkMutualPrev: string) => {
        const my_ElWarningFormArray = getFormArray(form, 'my_Warning.my_ElWarning');
        const posizAlboEntiControl = getDatiIdentificativiControl(form, 'my_PosizAlboEnti');
        // InfoPath: resets SezEnte on real CkMutualPrev changes only; emits so my_SezEnte
        // listener cascades the clear of DescSezEnte and TipoSezEnte
        getDatiIdentificativiControl(form, 'my_SezEnte').patchValue('', { emitEvent: false });

        if (toNum(my_CkMutualPrev) === 3) {
            posizAlboEntiControl.patchValue('Non iscritta', { emitEvent: false });
            my_ElWarningFormArray.controls.find((group: FormGroup) => {
                if (getControl(group, 'my_DescWarning').value === ALBO_WARNING_DESC) {
                    getControl(group, 'my_VisibWarning').patchValue('1', { emitEvent: false });
                }
            });
            toastrService.info("La cooperativa non è iscritta all'Albo. Occorre irrogare la diffida.", null, { timeOut: 1500 });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                datiId.my_SezEnte = '';
                datiId.my_PosizAlboEnti = 'Non iscritta';
                const warningEl = myVerbaleClone.my_Warning.my_ElWarning.find((el) => el.my_DescWarning === ALBO_WARNING_DESC);
                if (warningEl) warningEl.my_VisibWarning = '1';
                return myVerbaleClone;
            });
        } else {
            my_ElWarningFormArray.controls.find((group: FormGroup) => {
                if (getControl(group, 'my_DescWarning').value === ALBO_WARNING_DESC) {
                    getControl(group, 'my_VisibWarning').patchValue('0', { emitEvent: false });
                }
            });
            // InfoPath: clear PosizAlboEnti when user deselects value 3 and field was "Non iscritta"
            if (posizAlboEntiControl.value?.toLowerCase() === 'non iscritta') {
                posizAlboEntiControl.patchValue('', { emitEvent: false });
            }
            // InfoPath: prevalenti di diritto → force regime derogatorio; emits to cascade my_RegimeDerogatorioProvalenza listener
            if (my_CkMutualPrev === '4') {
                getDatiIdentificativiControl(form, 'my_RegimeDerogatorioProvalenza').patchValue('1');
            }
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                datiId.my_SezEnte = '';
                const warningEl = myVerbaleClone.my_Warning.my_ElWarning.find((el) => el.my_DescWarning === ALBO_WARNING_DESC);
                if (warningEl) warningEl.my_VisibWarning = '0';
                if (datiId.my_PosizAlboEnti?.toLowerCase() === 'non iscritta') {
                    datiId.my_PosizAlboEnti = '';
                }
                return myVerbaleClone;
            });
        }
    });
};

/** Disables my_PosizAlboEnti when my_CkMutualPrev is 3 (non iscritta), enables otherwise. Fires on init. */
const my_PosizAlboEntiEnableDisable = (form: FormGroup): void => {
    const ckMutualControl = getDatiIdentificativiControl(form, 'my_CkMutualPrev');
    const posizAlboEntiControl = getDatiIdentificativiControl(form, 'my_PosizAlboEnti');
    ckMutualControl.valueChanges.pipe(withInitialValueDebounced(ckMutualControl)).subscribe((value: string) => {
        if (toNum(value) === 3) {
            posizAlboEntiControl.disable({ emitEvent: false });
        } else {
            posizAlboEntiControl.enable({ emitEvent: false });
        }
    });
};

const my_SezEnte = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_SezEnteControl = getDatiIdentificativiControl(form, 'my_SezEnte');
    my_SezEnteControl.valueChanges.pipe(withChangeDebounced(my_SezEnteControl)).subscribe((my_SezEnte: string) => {
        if (!isNilOrEmpty(my_SezEnte)) {
            store
                .select(Revisione53DatiBusinessSelectors.getSezioneAlboNazEntiCoop)
                .pipe(take(1))
                .subscribe((sezionealbonazenticoop) => {
                    const found = sezionealbonazenticoop.find((s: Sezionealbonazenticoop) => s.id_sezioneAlboNazEntiCoop == my_SezEnte);
                    if (!found) return;
                    getDatiIdentificativiControl(form, 'my_DescSezEnte').patchValue(found.sezioneAlboNazEntiCoop, { emitEvent: false });
                    getDatiIdentificativiControl(form, 'my_TipoSezEnte').patchValue(found.Id_tipoSezione, { emitEvent: false });
                    updateVerbaleOnStore(store, (myVerbale) => {
                        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                        const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                        datiId.my_SezEnte = my_SezEnte;
                        datiId.my_DescSezEnte = found.sezioneAlboNazEntiCoop;
                        datiId.my_TipoSezEnte = found.Id_tipoSezione;
                        return myVerbaleClone;
                    });
                });
        } else {
            getDatiIdentificativiControl(form, 'my_DescSezEnte').patchValue('', { emitEvent: false });
            getDatiIdentificativiControl(form, 'my_TipoSezEnte').patchValue('', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                datiId.my_DescSezEnte = '';
                datiId.my_TipoSezEnte = '';
                return myVerbaleClone;
            });
        }
    });
};

const my_PosizAlboEnti = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_PosizAlboEntiControl = getDatiIdentificativiControl(form, 'my_PosizAlboEnti');
    my_PosizAlboEntiControl.valueChanges.pipe(withChangeDebounced(my_PosizAlboEntiControl)).subscribe((my_PosizAlboEnti: string) => {
        const v = (my_PosizAlboEnti?.trim() ?? '').toUpperCase();
        const alwaysValid = v === '' || v === 'NON ISCRITTA' || v === 'IN ATTESA';
        const isValid = alwaysValid || posizAlboEntiRegex.test(v);
        const validaValue = isValid ? '1' : '0';
        getControl(form, 'my_ValidaPosizAlboEnti').patchValue(validaValue, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_ValidaPosizAlboEnti = validaValue;
            return myVerbaleClone;
        });
    });
};

const my_CodAttiv = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_CodAttivControl = getDatiIdentificativiControl(form, 'my_CodAttiv');
    my_CodAttivControl.valueChanges.pipe(withChangeDebounced(my_CodAttivControl)).subscribe((my_CodAttiv: string) => {
        getControl(form, 'my_ValidaCodiceISTAT').patchValue('1', { emitEvent: false });
        if (isNilOrEmpty(my_CodAttiv)) {
            getControl(form, 'my_ValidaCodiceISTAT').patchValue('0', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_ValidaCodiceISTAT = '0';
                return myVerbaleClone;
            });
        } else {
            store
                .select(Revisione53DatiBusinessSelectors.getCodISTAT(my_CodAttiv))
                .pipe(take(1))
                .subscribe((codAttiv) => {
                    if (!codAttiv) {
                        getControl(form, 'my_ValidaCodiceISTAT').patchValue('0', { emitEvent: false });
                        getDatiIdentificativiControl(form, 'my_CodAttivDescriz').patchValue('', { emitEvent: false });
                        updateVerbaleOnStore(store, (myVerbale) => {
                            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                            myVerbaleClone.my_ValidaCodiceISTAT = '0';
                            myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CodAttivDescriz = '';
                            return myVerbaleClone;
                        });
                    } else {
                        const validaValue = my_CodAttiv.length >= 8 ? '1' : '0';
                        getControl(form, 'my_ValidaCodiceISTAT').patchValue(validaValue, { emitEvent: false });
                        getDatiIdentificativiControl(form, 'my_CodAttivDescriz').patchValue(codAttiv.descrizione, { emitEvent: false });
                        updateVerbaleOnStore(store, (myVerbale) => {
                            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                            myVerbaleClone.my_ValidaCodiceISTAT = validaValue;
                            myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CodAttivDescriz = codAttiv.descrizione;
                            return myVerbaleClone;
                        });
                    }
                });
        }
    });
};

const my_RegimeDerogatorioProvalenza = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_RegimeDerogatorioProvalenzaControl = getDatiIdentificativiControl(form, 'my_RegimeDerogatorioProvalenza');
    const my_DescRegimeDerogatorioPrevalenzaControl = getDatiIdentificativiControl(form, 'my_DescRegimeDerogatorioPrevalenza');
    const my_CkRientranteInRegimeDerogatorioControl = getDatiIdentificativiControl(form, 'my_CkRientranteInRegimeDerogatorio');
    my_RegimeDerogatorioProvalenzaControl.valueChanges.pipe(withChangeDebounced(my_RegimeDerogatorioProvalenzaControl)).subscribe((my_RegimeDerogatorioProvalenza: string) => {
        store
            .select(Revisione53DatiBusinessSelectors.getRegimeDerogatorio)
            .pipe(take(1))
            .subscribe((regimi) => {
                const desc = regimi?.find((r) => r.id_regimeDerogatorio === my_RegimeDerogatorioProvalenza)?.descrizione ?? '';
                my_DescRegimeDerogatorioPrevalenzaControl.patchValue(desc, { emitEvent: false });
                const ckValue = my_RegimeDerogatorioProvalenza === '1' || isNilOrEmpty(my_RegimeDerogatorioProvalenza) ? '0' : '1';
                my_CkRientranteInRegimeDerogatorioControl.patchValue(ckValue, { emitEvent: false });

                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                    const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                    datiId.my_RegimeDerogatorioProvalenza = my_RegimeDerogatorioProvalenza;
                    datiId.my_DescRegimeDerogatorioPrevalenza = desc;
                    datiId.my_CkRientranteInRegimeDerogatorio = ckValue;
                    return myVerbaleClone;
                });
            });
    });
};
